import { useEffect, useState, useMemo, Fragment, useContext } from 'react'
import moment from 'moment'

import { useModal } from '../../providers/ModalProvider/ModalProvider'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import {
  formatCurrency,
  formatDateTime,
  capitalizeString,
  createWorkbook,
  saveWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  styleTotalRow,
  styleSubTotalRow,
  addEmptyRows,
  styleEmptyRows,
  currencyFormat,
  DEFAULTNUMHEADERS,
  checkPermission,
} from '../../utilities/helpers'

import { getARAgingDetailReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'

import { ReportActions } from '../ReportActions'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { InvoiceHtml } from '../ModalContent/InvoiceHtml'

function computeGrandTotal(groupedData, key) {
  return groupedData.reduce((sum, g) => sum + (g?.[key] ?? 0), 0)
}

export default function ARAgingDetailsWrapper({ organization }) {
  const { openModal } = useModal()
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_DETAILS_REPORT
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [asOfDate, setAsOfDate] = useState(null)
  const [rows, setRows] = useState(null)

  const groupedData = useMemo(() => (rows ? rows : []), [rows])
  const grandAmountTotal = useMemo(
    () => computeGrandTotal(groupedData, 'amountSubtotal'),
    [groupedData]
  )
  const grandOpenBalanceTotal = useMemo(
    () => computeGrandTotal(groupedData, 'openBalanceSubtotal'),
    [groupedData]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!organization?.uuid) return
      if (!asOfDate) return
      setIsLoadingData(true)
      try {
        const res = await getARAgingDetailReport(
          organization.uuid,
          asOfDate.toISOString()
        )
        setRows(res.data)
      } catch (error) {
        console.error('Error fetching A/R aging detail:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    asOfDate,
    organization?.uuid,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const openInvoice = (order) => {
    openModal(
      InvoiceHtml,
      { order },
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  const handleExcelExport = async () => {
    try {
      if (!groupedData?.length || grandOpenBalanceTotal === 0) return

      const workbook = createWorkbook()
      const worksheet = addWorksheet('AR Aging Detail', workbook)

      const asOfDateStr = formatDateTime(moment(asOfDate), 'dateOnly')
      const headerLines = [`As of ${asOfDateStr}`]
      setHeaders(
        'A/R Aging Detail',
        organization?.name || '',
        worksheet,
        headerLines
      )

      const columns = [
        'section',
        'date_of_issue',
        'type',
        'payment_plan',
        'invoice_number',
        'customer',
        'due_date',
        'amount',
        'open_balance',
      ]
      const allRows = []
      const sectionMergeRanges = []
      const addToEmptyRows = []
      let bucketIndex = 0

      for (const {
        bucket,
        details,
        amountSubtotal,
        openBalanceSubtotal,
      } of groupedData) {
        if (details.length === 0) continue

        const firstDetailRowIndex = allRows.length

        // Detail rows - bucket label in first row's section, merge across all
        for (let i = 0; i < details.length; i++) {
          const d = details[i]
          const ob = d.openBalance ?? d.amount
          const dateStr =
            d.dateOfIssue && d.dateOfIssue !== '—'
              ? d.dateOfIssue.replace(/\//g, '-')
              : d.dateOfIssue
          const dueStr =
            d.dueDate && d.dueDate !== '—'
              ? d.dueDate.replace(/\//g, '-')
              : d.dueDate
          allRows.push({
            section: i === 0 ? bucket.label : '',
            date_of_issue: dateStr,
            type: d.paymentType,
            payment_plan: d.paymentPlan ?? 'No',
            invoice_number: d.invoiceNumber,
            customer: capitalizeString(d.customer),
            due_date: dueStr,
            amount: d.amount,
            open_balance: ob,
            _rowType: 'detail',
          })
        }

        if (details.length > 1) {
          sectionMergeRanges.push({
            startRow: firstDetailRowIndex,
            endRow: firstDetailRowIndex + details.length - 1,
            bucketIndex,
          })
        }

        bucketIndex += 1

        // Subtotal row
        addToEmptyRows.push(allRows.length)
        allRows.push({
          section: `Total for ${bucket.label}`,
          date_of_issue: '',
          type: '',
          payment_plan: '',
          invoice_number: '',
          customer: '',
          due_date: '',
          amount: amountSubtotal ?? 0,
          open_balance: openBalanceSubtotal ?? 0,
          _rowType: 'subtotal',
        })
      }

      // Grand total row
      addToEmptyRows.push(allRows.length)
      allRows.push({
        section: 'TOTAL',
        date_of_issue: '',
        type: '',
        payment_plan: '',
        invoice_number: '',
        customer: '',
        due_date: '',
        amount: grandAmountTotal,
        open_balance: grandOpenBalanceTotal,
        _rowType: 'grandTotal',
      })

      const formatRow = (row) => ({
        section: row.section,
        date_of_issue: row.date_of_issue,
        type: row.type,
        payment_plan: row.payment_plan,
        invoice_number: row.invoice_number,
        customer: row.customer,
        due_date: row.due_date,
        amount: row.amount,
        open_balance: row.open_balance,
      })

      setColumnsAndRows(columns, allRows, formatRow, worksheet)

      const headerRows = DEFAULTNUMHEADERS + headerLines.length

      currencyFormat(8, worksheet, headerRows)
      currencyFormat(9, worksheet, headerRows)

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRows) return

        const rowData = allRows[rowNumber - headerRows - 1]
        const rowType = rowData?._rowType

        if (rowType === 'detail' && rowData?.section) {
          const sectionCell = row.getCell(1)
          sectionCell.font = { bold: true }
        } else if (rowType === 'subtotal') {
          styleSubTotalRow(row, worksheet, false)
        } else if (rowType === 'grandTotal') {
          styleTotalRow(row, worksheet, false)
        }
      })

      // Add empty rows between subtotals and next bucket labels, and before grand total
      const rowsToInsertAfter = addToEmptyRows
        .slice(0, -1)
        .map((idx) => headerRows + idx + 1) //  converts allRows indices to worksheet rows:
      addEmptyRows(rowsToInsertAfter, worksheet)

      // Build set of subtotal row numbers after insert (using _rowType from allRows)
      const subtotalRowNumbers = new Set(
        allRows
          .map((row, idx) => (row._rowType === 'subtotal' ? idx : -1))
          .filter((idx) => idx >= 0)
          .map((idx) => {
            const origRow = headerRows + 1 + idx
            const shift = rowsToInsertAfter.filter(
              (insertAfter) => insertAfter + 1 <= origRow
            ).length
            return origRow + shift
          })
      )

      // Style the inserted empty rows (row after each subtotal)
      const lastColumnNumber = worksheet.lastColumn.number
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRows) return
        if (subtotalRowNumbers.has(rowNumber)) {
          const emptyRow = worksheet.getRow(rowNumber + 1)
          styleEmptyRows(emptyRow, lastColumnNumber, false)
        }
      })

      // Merge section cells AFTER empty row inserts (row indices have shifted)
      sectionMergeRanges.forEach(({ startRow, endRow, bucketIndex: bi }) => {
        const rowsInsertedBeforeThisBucket = bi
        const wsStartRow =
          headerRows + 1 + startRow + rowsInsertedBeforeThisBucket
        const wsEndRow = headerRows + 1 + endRow + rowsInsertedBeforeThisBucket
        worksheet.mergeCells(`A${wsStartRow}:A${wsEndRow}`)
        const sectionCell = worksheet.getCell(wsStartRow, 1)
        sectionCell.font = { bold: true }
      })

      await saveWorkbook(workbook, 'ar-aging-detail')
    } catch (err) {
      console.error('A/R Aging Detail Excel export failed:', err)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  return (
    <section
      className="spacer-sm"
      data-testid="ar-aging-details-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>A/R Aging Detail Report</h1>
        </div>
        <p className="section-header-desc">
          View outstanding balances grouped by aging period.
        </p>
      </header>

      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected={asOfDate}
        startDate={asOfDate}
        setStartDate={setAsOfDate}
        showTime={false}
        data={rows}
        downloadReport={handleExcelExport}
      />

      <Results
        isLoading={isLoadingData}
        groupedData={groupedData}
        grandAmountTotal={grandAmountTotal}
        grandOpenBalanceTotal={grandOpenBalanceTotal}
        asOfDate={asOfDate}
        organization={organization}
        openInvoice={openInvoice}
      />
    </section>
  )
}

const TableHeader = () => (
  <thead>
    <tr className="md">
      <th className="text-left uppercase text-body">Section</th>
      <th className="text-left uppercase text-body ps-4">Date of Issue</th>
      <th className="text-left uppercase text-body sm">Type</th>
      <th className="text-left uppercase text-body sm">Payment Plan</th>
      <th className="text-left uppercase text-body">Invoice #</th>
      <th className="text-left uppercase text-body">Customer</th>
      <th className="text-left uppercase text-body">Due Date</th>
      <th className="text-end uppercase text-body sm">Amount</th>
      <th className="text-end uppercase text-body lg">Open Balance</th>
    </tr>
  </thead>
)

const DetailRowWithSection = ({ row, sectionLabel, rowSpan, openInvoice }) => (
  <tr className="md">
    <td
      rowSpan={rowSpan}
      className="text-left align-top fw-semibold border-right"
    >
      {sectionLabel}
    </td>
    <DetailsTableCell row={row} showPadFirst={true} openInvoice={openInvoice} />
  </tr>
)

const DetailsTableCell = ({ row, showPadFirst = false, openInvoice }) => (
  <>
    <td className={`text-left ${showPadFirst ? 'ps-4' : ''}`}>
      {row.dateOfIssue}
    </td>
    <td className="text-left">{row.paymentType}</td>
    <td className="text-left">{row.paymentPlan ?? 'No'}</td>
    <td className="text-left">
      <Button
        variant="link"
        className="p-0 text-link"
        onClick={() => openInvoice(row.order)}
      >
        {row.invoiceNumber}
      </Button>
    </td>
    <td className="text-left">{capitalizeString(row.customer)}</td>
    <td className="text-left">{row.dueDate}</td>
    <td className="text-end">{formatCurrency(row.amount)}</td>
    <td className="text-end">
      {formatCurrency(row.openBalance ?? row.amount)}
    </td>
  </>
)

const DetailRow = ({ row, openInvoice }) => (
  <tr className="md">
    <DetailsTableCell row={row} openInvoice={openInvoice} />
  </tr>
)

const SubtotalRow = ({ label, amount, openBalance }) => (
  <tr className="md total-row light">
    <td className="text-left">Total for {label}</td>
    <td colSpan={6} className="text-left"></td>
    <td className="text-end">{formatCurrency(amount)}</td>
    <td className="text-end">{formatCurrency(openBalance)}</td>
  </tr>
)

function Results({
  isLoading,
  groupedData,
  grandAmountTotal,
  grandOpenBalanceTotal,
  asOfDate,
  organization,
  openInvoice,
}) {
  if (!asOfDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing an as of date</p>
          <p className="subtitle subtitle--dark">
            Select a date above and the report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  const hasData = groupedData.some((g) => g.details.length > 0)

  if (!hasData) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No results found for that date.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">A/R Aging Detail Report</Card.Title>
        <Card.Subtitle className="text-muted mt-2 fst-italic">
          As of {formatDateTime(moment(asOfDate), 'dateOnly')}
        </Card.Subtitle>
      </div>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height--scrollable">
          <Table responsive>
            <TableHeader />
            <tbody>
              {groupedData.map(
                ({ bucket, details, amountSubtotal, openBalanceSubtotal }) =>
                  details.length > 0 && (
                    <Fragment key={bucket.key}>
                      {details.map((row, idx) =>
                        idx === 0 ? (
                          <DetailRowWithSection
                            key={`${bucket.key}-${row.invoiceNumber}-${idx}`}
                            row={row}
                            sectionLabel={bucket.label}
                            rowSpan={details.length}
                            openInvoice={openInvoice}
                          />
                        ) : (
                          <DetailRow
                            key={`${bucket.key}-${row.invoiceNumber}-${idx}`}
                            row={row}
                            openInvoice={openInvoice}
                          />
                        )
                      )}
                      <SubtotalRow
                        label={bucket.label}
                        amount={amountSubtotal ?? 0}
                        openBalance={openBalanceSubtotal ?? 0}
                      />
                      <tr className="white">
                        <td colSpan={9} className="td--empty"></td>
                      </tr>
                    </Fragment>
                  )
              )}
              <tr className="md total-row dark">
                <td className="text-left">TOTAL</td>
                <td colSpan={6} className="text-left"></td>
                <td className="text-end">{formatCurrency(grandAmountTotal)}</td>
                <td className="text-end">
                  {formatCurrency(grandOpenBalanceTotal)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}
